import { forwardRef } from 'react'
import { Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'

/**
 * The one button. Audit baseline: 195 hand-written primary buttons in 9+ padding
 * and 3 text-size variants. Colours were already consistent; shape and size were not.
 *
 *   variant  'primary' (filled brand — one per area, the main action)
 *            'secondary' (white + border — ยกเลิก, secondary actions)
 *            'ghost' (no border — toolbar / low-emphasis)
 *            'soft' (brand-50 — "แก้ไข" on read views)
 *            'danger' (filled rose — ONLY inside a destructive confirm)
 *            'danger-soft' (rose text — a "ลบ" entry point that then confirms)
 *   size     'md' (default, page headers / footers) | 'sm' (inside cards, tables, filters)
 *   icon     a lucide component, drawn before the label
 *   loading  swaps the icon for a spinner and disables the button
 *   to       render as a router <Link> instead of a <button>
 *   disabledReason  why it is disabled — shown as a tooltip (a disabled <button> fires no
 *            hover events, so the reason sits on a wrapper). "ไม่มีสิทธิ์แก้ไข", "วันนี้ปิดยอดแล้ว"
 */
const VARIANT = {
  primary: 'bg-brand-600 hover:bg-brand-700 text-white shadow-sm',
  secondary: 'bg-white text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-slate-50 hover:ring-slate-300',
  ghost: 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
  soft: 'bg-brand-50 text-brand-700 hover:bg-brand-100',
  danger: 'bg-rose-600 hover:bg-rose-700 text-white shadow-sm',
  'danger-soft': 'text-rose-600 hover:bg-rose-50',
}
const SIZE = {
  md: 'h-9 px-3.5 gap-1.5 rounded-xl text-[13.5px]',
  sm: 'h-8 px-2.5 gap-1 rounded-lg text-[13px]',
}
const ICON = { md: 15, sm: 14 }

export const Button = forwardRef(function Button(
  { variant = 'primary', size = 'md', icon: Icon, loading = false, disabled = false, disabledReason, to, type = 'button', className = '', children, ...rest },
  ref,
) {
  const cls = `inline-flex items-center justify-center font-semibold whitespace-nowrap transition-colors
    disabled:opacity-50 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300
    ${VARIANT[variant] || VARIANT.primary} ${SIZE[size] || SIZE.md} ${className}`
  const glyph = loading
    ? <Loader2 size={ICON[size]} className="animate-spin" />
    : Icon ? <Icon size={ICON[size]} strokeWidth={2.25} /> : null
  if (to && !disabled && !loading) {
    return <Link ref={ref} to={to} className={cls} {...rest}>{glyph}{children}</Link>
  }
  const btn = (
    <button ref={ref} type={type} disabled={disabled || loading} className={cls} {...rest}>
      {glyph}{children}
    </button>
  )
  return disabled && disabledReason ? <span className="inline-flex" title={disabledReason}>{btn}</span> : btn
})

/**
 * Square icon-only button. `label` is required: it becomes the tooltip and the
 * screen-reader name. `disabledReason` — when disabled, the tooltip says why instead
 * (on a wrapper, since a disabled button fires no hover events).
 */
export const IconButton = forwardRef(function IconButton(
  { icon: Icon, label, size = 'md', variant = 'ghost', disabledReason, className = '', ...rest },
  ref,
) {
  const box = size === 'sm' ? 'w-7 h-7 rounded-lg' : 'w-8 h-8 rounded-lg'
  const tone = VARIANT[variant] || VARIANT.ghost
  const btn = (
    <button ref={ref} type="button" title={label} aria-label={label}
      className={`inline-flex items-center justify-center transition-colors disabled:opacity-40 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 ${box} ${tone} ${className}`}
      {...rest}>
      <Icon size={size === 'sm' ? 14 : 16} strokeWidth={2} />
    </button>
  )
  return rest.disabled && disabledReason ? <span className="inline-flex" title={disabledReason}>{btn}</span> : btn
})
